import { Injectable } from '@angular/core';
import { registerPlugin } from '@capacitor/core';
import { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications, PermissionStatus as NotificationPermissionStatus } from '@capacitor/local-notifications';
import { NativeSettings, IOSSettings, AndroidSettings } from 'capacitor-native-settings';
import { Preferences } from '@capacitor/preferences';
import { LocationService } from './location.service';
import { Capacitor } from '@capacitor/core';
import { AlertController, ToastController } from '@ionic/angular/standalone';
import { signal, WritableSignal } from '@angular/core';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
const NativeBattery = registerPlugin<{
    isBatteryOptimizationEnabled: () => Promise<{ isOptimizing: boolean }>,
    requestIgnoreBatteryOptimizations: () => Promise<void>
}>('NativeBattery');

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly ONBOARDING_KEY = 'has_completed_location_onboarding';
    public permissionsStatus: WritableSignal<boolean> = signal(false);

    constructor(
        private locationService: LocationService,
        private alertController: AlertController,
        private toastController: ToastController
    ) {
        this.checkInitialStatus();
    }

    private async checkInitialStatus() {
        // Bypass para WEB: En computadora siempre decimos que los permisos están OK
        if (Capacitor.getPlatform() === 'web') {
            this.permissionsStatus.set(true);
            return;
        }

        try {
            const loc = await Geolocation.checkPermissions();
            const notif = await LocalNotifications.checkPermissions();
            this.permissionsStatus.set(loc.location === 'granted' && notif.display === 'granted');
        } catch (e) {
            this.permissionsStatus.set(false);
        }
    }

    async requestOnboardingPermissions(manual = false): Promise<void> {
        console.log('[Permissions] Starting onboarding permission chain...');

        // Si es WEB, simplemente marcamos como completado y salimos
        if (Capacitor.getPlatform() === 'web') {
            console.warn('[Permissions] Platform is WEB. Skipping native permission chain.');
            this.permissionsStatus.set(true);
            await Preferences.set({ key: this.ONBOARDING_KEY, value: 'true' });
            return;
        }

        // Only show the full disclaimer+request flow once per install.
        // After the user has gone through it, only run a silent status refresh.
        const alreadyOnboarded = await this.hasCompletedOnboarding();
        if (alreadyOnboarded) {
            console.log('[Permissions] Onboarding already completed, running silent status check.');
            await this.checkInitialStatus();
            return;
        }

        let disclaimerShown = false;
        try {
            const status = await Geolocation.checkPermissions();
            console.log('[Permissions] Permission status:', status.location);
            if (status.location !== 'granted') {
                const disclaimerAccepted = await this.showLocationDisclaimer();
                disclaimerShown = true;
                if (!disclaimerAccepted) {
                    console.log('[Permissions] User rejected onboarding disclaimer.');
                    await Preferences.set({ key: this.ONBOARDING_KEY, value: 'true' });
                    return;
                }
            } else {
                console.log('[Permissions] Permission already granted, skipping disclaimer');
            }
        } catch (e) {
            console.error('[Permissions] Error checking location status:', e);
        }

        // Pass disclaimerShown so repairPermissions doesn't show it again
        await this.repairPermissions(false, disclaimerShown);
        await Preferences.set({ key: this.ONBOARDING_KEY, value: 'true' });
    }

    // ── Camera permission (for QR passenger scanner) ─────────────────────────

    async requestCameraPermission(manual = false): Promise<boolean> {
        if (Capacitor.getPlatform() === 'web') return true;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(t => t.stop());
            return true;
        } catch (e: any) {
            if (e?.name === 'NotAllowedError' && manual) {
                await this.showPermissionAlert(
                    'Cámara desactivada',
                    'Para escanear el QR del pasajero, necesitamos acceso a la cámara.',
                    'Cámara'
                );
            }
            return false;
        }
    }

    async hasCompletedOnboarding(): Promise<boolean> {
        const { value } = await Preferences.get({ key: this.ONBOARDING_KEY });
        return value === 'true';
    }

    async repairPermissions(manual: boolean = false, disclaimerAlreadyShown: boolean = false): Promise<void> {
        console.log(`[Permissions] Repairing permissions (manual=${manual}, disclaimerAlreadyShown=${disclaimerAlreadyShown})...`);

        // Bypass para WEB: No ejecutar nada de lo que sigue si no es móvil
        if (Capacitor.getPlatform() === 'web') {
            console.log('[Permissions] Repair skipped: Web platform detected.');
            this.permissionsStatus.set(true);
            if (manual) await this.showSuccessToast();
            return;
        }

        if (this.locationService.isTripActive && !manual) {
            console.log('[Permissions] Skipping auto-repair: trip is active.');
            return;
        }

        // When manually triggered (verificar permisos), show disclaimer FIRST
        // Skip if disclaimer was already shown by requestOnboardingPermissions
        if (manual && !disclaimerAlreadyShown) {
            const disclaimerAccepted = await this.showLocationDisclaimer();
            if (!disclaimerAccepted) {
                console.log('[Permissions] User rejected disclaimer.');
                return;
            }
            // Modal already dismissed and waited in showLocationDisclaimer()
        }

        try {
            const status = await Geolocation.checkPermissions();
            console.log('[Permissions] Current status:', status.location);

            // When manually triggered (verificar permisos), ALWAYS try to request/escalate to "Always"
            // regardless of current status - this handles denied, prompt, and WhenInUse cases
            if (manual) {
                console.log('[Permissions] Manual trigger - requesting Always permission...');
                if (Capacitor.getPlatform() === 'android') {
                    await this.handleAndroidBackgroundEscalation(manual, disclaimerAlreadyShown);
                } else {
                    await this.requestLocationPermissionIOS();
                }
                await this.showSuccessToast();
            } else {
                // Auto-triggered (onboarding) - only request if not granted
                if (status.location !== 'granted') {
                    console.log('[Permissions] Auto trigger - requesting permission...');
                    if (Capacitor.getPlatform() === 'android') {
                        await this.handleAndroidBackgroundEscalation(manual, disclaimerAlreadyShown);
                    } else {
                        await this.requestLocationPermissionIOS();
                    }
                } else {
                    console.log('[Permissions] Permission already granted, skipping request');
                }
            }
        } catch (e: any) {
            console.error('[Permissions] Error during permission check:', e);
        }

        // 2. Battery Optimization (Android only)
        if (Capacitor.getPlatform() === 'android') {
            await this.checkBatteryOptimization(manual);
        }

        // 3. Notification Permissions
        const notifStatus = await LocalNotifications.checkPermissions();
        if (notifStatus.display === 'prompt') {
            const notifResult = await LocalNotifications.requestPermissions();
            if (notifResult.display === 'granted') {
                await this.createNotificationChannel();
            }
        }

        // 5. Update overall status signal
        const finalLoc = await Geolocation.checkPermissions();
        const finalNotif = await LocalNotifications.checkPermissions();
        const isOk = finalLoc.location === 'granted' && finalNotif.display === 'granted';
        this.permissionsStatus.set(isOk);
    }



    private async showSuccessToast(): Promise<void> {
        const toast = await this.toastController.create({
            message: 'Permisos habilitados',
            duration: 2500,
            position: 'bottom',
            color: 'success',
            icon: 'checkmark-circle-outline',
        });
        await toast.present();
    }

    private async showPermissionAlert(header: string, message: string, settingsCtx: string) {
        const alert = await this.alertController.create({
            header,
            message,
            buttons: [
                { text: 'Cancelar', role: 'cancel' },
                { text: 'Ir a Ajustes', handler: () => { this.openSettings(settingsCtx); } }
            ],
            mode: 'ios'
        });
        await alert.present();
    }

    private async openSettings(permissionName: string) {
        await NativeSettings.open({
            optionAndroid: AndroidSettings.ApplicationDetails,
            optionIOS: IOSSettings.App
        });
    }

    async requestNotificationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
        if (Capacitor.getPlatform() === 'web') return 'granted';
        const status = await LocalNotifications.checkPermissions();
        if (status.display === 'prompt') {
            const result = await LocalNotifications.requestPermissions();
            if (result.display === 'granted') await this.createNotificationChannel();
            return result.display as any;
        }
        return status.display as any;
    }

    private async createNotificationChannel(): Promise<void> {
        try {
            await LocalNotifications.createChannel({
                id: 'checkpoints',
                name: 'Puntos de Control',
                description: 'Alertas de recorrido',
                importance: 5,
                vibration: true,
                visibility: 1,
            });
        } catch (e) { }
    }

    /**
     * Shows the location disclaimer modal.
     * Returns true if user clicked "Continuar", false if "Rechazar".
     * Waits for modal to fully dismiss before returning.
     */
    private async showLocationDisclaimer(): Promise<boolean> {
        console.log('[Permissions] Creating disclaimer modal...');
        const alert = await this.alertController.create({
            header: 'Acceso a ubicación en segundo plano',
            message:
                'syncc necesita acceder a tu ubicación en todo momento, incluso cuando la aplicación está cerrada o en segundo plano.\n\n' +
                'Esto es necesario para rastrear tu recorrido y enviar tu posición en tiempo real mientras tienes un viaje activo.\n\n' +
                'En la siguiente pantalla selecciona "Permitir siempre".',
            backdropDismiss: false,
            mode: 'ios',
            buttons: [
                { text: 'Entendido', role: 'confirm' },
            ],
        });

        console.log('[Permissions] Presenting disclaimer modal...');
        await alert.present();

        console.log('[Permissions] Waiting for user to click...');
        // Wait for user to click a button
        const { role } = await alert.onDidDismiss();
        console.log('[Permissions] User clicked, role:', role);

        // Wait an additional 400ms for the dismiss animation to fully complete
        console.log('[Permissions] Waiting 400ms for modal dismiss animation...');
        await new Promise(resolve => setTimeout(resolve, 400));
        console.log('[Permissions] Disclaimer flow complete, returning:', role === 'confirm');

        // Returns true when user tapped "Entendido" (always proceeds to system dialog)
        return role === 'confirm';
    }

    /**
     * Requests location permission on iOS and triggers background geolocation escalation.
     * Step 1: Geolocation.requestPermissions() shows the "Allow When In Use" native dialog.
     * Step 2: BackgroundGeolocation.addWatcher escalates WhenInUse → Always.
     * Both steps are required — skipping step 1 means iOS never shows the initial dialog.
     */
    private async requestLocationPermissionIOS(): Promise<void> {
        // Step 1: request WhenInUse permission (shows the iOS native modal)
        const result = await Geolocation.requestPermissions();
        console.log('[Permissions] iOS location request result:', result.location);

        // Step 2: escalate to Always via BackgroundGeolocation
        if (result.location === 'granted' || result.location === 'denied') {
            try {
                const tempId = await BackgroundGeolocation.addWatcher(
                    {
                        backgroundTitle: 'Rastreo de Viaje',
                        backgroundMessage: "Syncc está compartiendo tu ubicación mientras tienes un viaje activo.",
                        requestPermissions: true,
                        stale: false,
                    },
                    () => { }
                );
                setTimeout(() => BackgroundGeolocation.removeWatcher({ id: tempId }), 5000);
            } catch (e) {
                console.error('[Permissions] Background geolocation watcher error:', e);
            }
        }
    }

    private async handleAndroidBackgroundEscalation(manual: boolean, disclaimerAlreadyShown: boolean = false): Promise<void> {
        // Google Play requires a prominent disclosure before requesting background location.
        // Skip if already shown by the caller (requestOnboardingPermissions or repairPermissions).
        if (!manual && !disclaimerAlreadyShown) {
            const accepted = await this.showLocationDisclaimer();
            if (!accepted) return;
        }

        // Step 1: request foreground location permission (shows native Android modal)
        const result = await Geolocation.requestPermissions();
        console.log('[Permissions] Android location request result:', result.location);

        // Step 2: if granted, escalate to "Allow all the time" via BackgroundGeolocation
        if (result.location === 'granted') {
            try {
                const tempId = await BackgroundGeolocation.addWatcher(
                    {
                        backgroundTitle: 'Rastreo de Viaje',
                        backgroundMessage: "Syncc está compartiendo tu ubicación mientras tienes un viaje activo.",
                        requestPermissions: true,
                        stale: false,
                    },
                    () => { }
                );
                setTimeout(() => BackgroundGeolocation.removeWatcher({ id: tempId }), 5000);
            } catch (e) {
                console.error('[Permissions] Background geolocation watcher error:', e);
            }
        } else if (result.location === 'denied') {
            await this.showPermissionAlert(
                'Permiso de ubicación denegado',
                'Para usar syncc, necesitas habilitar el acceso a la ubicación en Ajustes.',
                'Ubicación'
            );
        }
    }

    private async checkBatteryOptimization(manual: boolean): Promise<void> {
        try {
            const { isOptimizing } = await NativeBattery.isBatteryOptimizationEnabled();
            if (isOptimizing) {
                const manufacturer = await this.getDeviceManufacturer();
                const instructions = this.getOEMInstructions(manufacturer);

                const alert = await this.alertController.create({
                    header: 'Optimización de Batería',
                    message: `Para un rastreo continuo en segundo plano, necesitamos desactivar la optimización de batería.\n\n${instructions}`,
                    buttons: [
                        { text: 'Más tarde', role: 'cancel' },
                        {
                            text: 'Desactivar',
                            handler: async () => {
                                try {
                                    await NativeBattery.requestIgnoreBatteryOptimizations();
                                    const toast = await this.toastController.create({
                                        message: '✓ Optimización desactivada',
                                        duration: 2000,
                                        position: 'bottom',
                                        color: 'success'
                                    });
                                    await toast.present();
                                } catch (e) {
                                    console.warn('[Permissions] Battery optimization request failed:', e);
                                }
                            }
                        }
                    ]
                });
                await alert.present();
            }
        } catch (e) {
            console.warn('[Permissions] Battery optimization check failed:', e);
        }
    }

    private async getDeviceManufacturer(): Promise<string> {
        try {
            // Detectar fabricante del dispositivo usando user agent o device info
            const ua = navigator.userAgent.toLowerCase();
            if (ua.includes('xiaomi') || ua.includes('redmi') || ua.includes('poco')) return 'xiaomi';
            if (ua.includes('huawei') || ua.includes('honor')) return 'huawei';
            if (ua.includes('samsung')) return 'samsung';
            if (ua.includes('oppo')) return 'oppo';
            if (ua.includes('vivo')) return 'vivo';
            if (ua.includes('oneplus')) return 'oneplus';
            return 'generic';
        } catch {
            return 'generic';
        }
    }

    private getOEMInstructions(manufacturer: string): string {
        const instructions: Record<string, string> = {
            xiaomi: '📱 Xiaomi/Redmi: Después de desactivar, ve a Ajustes > Apps > syncc > Ahorro de batería > Sin restricciones.',
            huawei: '📱 Huawei/Honor: Después de desactivar, ve a Ajustes > Batería > Inicio de apps > syncc > Administrar manualmente (activar todo).',
            samsung: '📱 Samsung: Después de desactivar, ve a Ajustes > Batería > Uso de batería en segundo plano > syncc > Sin restricciones.',
            oppo: '📱 Oppo: Después de desactivar, ve a Ajustes > Batería > Ahorro de energía > syncc > Permitir actividad en segundo plano.',
            vivo: '📱 Vivo: Después de desactivar, ve a Ajustes > Batería > Consumo de batería en segundo plano > syncc > Permitir consumo elevado.',
            oneplus: '📱 OnePlus: Después de desactivar, ve a Ajustes > Batería > Optimización de batería > syncc > No optimizar.',
            generic: '💡 Sugerencia: Verifica en Ajustes de tu teléfono que syncc no esté en modo de ahorro de batería.'
        };
        return instructions[manufacturer] || instructions['generic'];
    }
}